import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, MapPin, X } from 'lucide-react';
import { Button, Input } from '@/components/ui';
import { consoleLocationService } from '@/services/consoleLocationService';
import { useNotification } from '@/hooks/useNotification';

interface CreateCustomLocationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated: (id: string) => void;
  /**
   * Pre-select the district/subdistrict when the user opens the
   * modal from an already-filtered grid. Empty strings mean
   * "let the user pick".
   */
  initialDistrict?: string;
  initialSubdistrict?: string;
}

/**
 * ADMIN modal to POST /console/location/customLocation/:district/:subdistrict/:name.
 *
 * The endpoint requires an existing (district, subdistrict) pair —
 * both come from the cascade browse endpoint so the user cannot
 * type a typo that the server would reject with "Invalid location
 * data". The custom-location name is a free-form text input (the
 * doc has no length or character rules beyond URL-encoding).
 */
export const CreateCustomLocationModal = ({
  isOpen,
  onClose,
  onCreated,
  initialDistrict,
  initialSubdistrict,
}: CreateCustomLocationModalProps) => {
  const { showSuccess } = useNotification();
  const queryClient = useQueryClient();

  const [district, setDistrict] = useState('');
  const [subdistrict, setSubdistrict] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setDistrict(initialDistrict ?? '');
    setSubdistrict(initialSubdistrict ?? '');
    setName('');
    setError(null);
  }, [isOpen, initialDistrict, initialSubdistrict]);

  useEffect(() => {
    if (!isOpen) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [isOpen]);

  const districtsQuery = useQuery({
    queryKey: ['console-location', 'districts'],
    queryFn: () => consoleLocationService.getDistricts(),
    enabled: isOpen,
    staleTime: 60 * 60 * 1000,
  });

  const subdistrictsQuery = useQuery({
    queryKey: ['console-location', 'subdistricts', district],
    queryFn: () => consoleLocationService.getSubdistricts(district),
    // Only fire when the user has actually picked a district — the
    // endpoint requires one, and the empty-string call would 404.
    enabled: isOpen && district.length > 0,
    staleTime: 60 * 60 * 1000,
  });

  const createMutation = useMutation({
    mutationFn: () =>
      consoleLocationService.createCustomLocation(district, subdistrict, name.trim()),
    onSuccess: (response) => {
      showSuccess('Custom location added', `${name.trim()} → ${response.id}`);
      // Invalidate every search+cascade query so the new row shows
      // up on the next page render without a manual refresh.
      queryClient.invalidateQueries({ queryKey: ['console-location'] });
      onCreated(response.id);
      onClose();
    },
    onError: (err: any) => {
      setError(err?.message || 'Failed to create custom location.');
    },
  });

  const canSubmit = useMemo(
    () => district.length > 0 && subdistrict.length > 0 && name.trim().length > 0,
    [district, subdistrict, name],
  );

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    if (!canSubmit || createMutation.isPending) return;
    createMutation.mutate();
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40"
      role="dialog"
      aria-modal="true"
      aria-labelledby="create-custom-location-title"
      onClick={onClose}
    >
      <form
        className="bg-white rounded-lg shadow-xl max-w-lg w-full overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        onSubmit={handleSubmit}
      >
        <div className="flex items-start justify-between px-6 py-4 border-b border-rgf-gray-200">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 p-2 rounded-lg bg-rgf-primary-100">
              <MapPin className="h-5 w-5 text-rgf-primary-600" />
            </div>
            <div>
              <h3
                id="create-custom-location-title"
                className="text-lg font-semibold text-rgf-gray-900"
              >
                Add custom location
              </h3>
              <p className="text-xs text-rgf-gray-500 mt-0.5">
                Registers a `C_*` row under an existing subdistrict.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded-md hover:bg-rgf-gray-100 text-rgf-gray-500"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-6 py-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-rgf-gray-700 mb-1">
              District
            </label>
            <select
              value={district}
              onChange={(e) => {
                setDistrict(e.target.value);
                // Subdistrict list is district-scoped — clear the
                // previous pick so we don't POST a stale pair.
                setSubdistrict('');
              }}
              disabled={districtsQuery.isLoading}
              className="w-full px-3 py-2 border border-rgf-gray-300 rounded-lg focus:ring-2 focus:ring-rgf-primary-500 focus:border-rgf-primary-500"
              required
            >
              <option value="">
                {districtsQuery.isLoading ? 'Loading districts…' : 'Select district'}
              </option>
              {(districtsQuery.data ?? []).map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-rgf-gray-700 mb-1">
              Subdistrict
            </label>
            <select
              value={subdistrict}
              onChange={(e) => setSubdistrict(e.target.value)}
              disabled={!district || subdistrictsQuery.isLoading}
              className="w-full px-3 py-2 border border-rgf-gray-300 rounded-lg focus:ring-2 focus:ring-rgf-primary-500 focus:border-rgf-primary-500 disabled:bg-rgf-gray-50 disabled:text-rgf-gray-500"
              required
            >
              <option value="">
                {!district
                  ? 'Pick a district first'
                  : subdistrictsQuery.isLoading
                    ? 'Loading subdistricts…'
                    : 'Select subdistrict'}
              </option>
              {(subdistrictsQuery.data ?? []).map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-rgf-gray-700 mb-1">
              Location name
            </label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. New Community Center"
              maxLength={128}
              required
            />
            <p className="text-xs text-rgf-gray-500 mt-1">
              Must be unique under the selected subdistrict. Backend rejects duplicates.
            </p>
          </div>

          {error && (
            <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
              <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 px-6 py-3 bg-rgf-gray-50 border-t border-rgf-gray-200">
          <Button
            variant="secondary"
            size="sm"
            type="button"
            onClick={onClose}
            disabled={createMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            type="submit"
            disabled={!canSubmit || createMutation.isPending}
          >
            {createMutation.isPending ? 'Creating…' : 'Create'}
          </Button>
        </div>
      </form>
    </div>
  );
};
